import { darken } from '@mantine/core';
import customImageSVG from '../../assets/layer-pins/pin-customImage.svg?raw';

/**
 * Generates an SVG data URL for a map pin by replacing `{bgColor}` and
 * `{bgColorShdw}` placeholders in a pre-built pin SVG template with the
 * given fill color (and a darker variant).
 *
 * @param pin - The raw pin SVG template string with `{bgColor}` and `{bgColorShdw}` placeholders.
 * @param bgColor - Fill color for the pin, defaulting to purple `#4f2e91`.
 * @returns A `data:image/svg+xml,...` URI string with the pin background colors substituted.
 */
export const customizePin = ({
  bgColor = '#4f2e91',
  pin
}: { bgColor?: string; pin: string }) => {
  const svg = pin.replace('{bgColorShdw}', darken(bgColor, 0.2)).replace('{bgColor}', bgColor);
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/**
 * Normalizes a URL by encoding `&` characters to `&amp;`, which is
 * safe for use as an SVG `<image>` href or `src` attribute.
 *
 * If the input cannot be parsed as a URL it returns the input with all
 * `&` replaced by `&amp;`. Returns the input unchanged if it already contains `&amp;`.
 *
 * @param input - The URL string to normalize.
 * @returns The normalized URL with `&` replaced by `&amp;`, or `input` unchanged if it already contains `&amp;`.
 */
export function toSvgHref(input: string): string {
  if (input.includes('&amp;')) return input;
  let url: URL;
  try {
    url = new URL(input);
  } catch {
    return input.replace(/&/g, '&amp;');
  }
  const normalized = new URL(url.toString());
  const params = new URLSearchParams();
  for (const [key, value] of normalized.searchParams.entries()) {
    params.set(key, value);
  }
  normalized.search = params.toString();
  return normalized.toString().replace(/&/g, '&amp;');
}
/**
 * Generates an SVG data URL for a map pin by substituting placeholders
 * in a pre-built SVG template with the given background color (and a
 * darker variant) and an image URL.
 *
 * @param bgColor - Fill color for the pin, defaulting to purple `#4f2e91`.
 * @param imgUrl - Optional image URL to embed in the pin SVG.
 * @returns A `data:image/svg+xml,...` URI string with the pin background colors and image substituted.
 */
export const customizeImagePin = ({
  bgColor = '#4f2e91',
  imgUrl = ''
}: { bgColor?: string; imgUrl?: string }) => {
  const svg = customImageSVG.replace('{bgColorShdw}', darken(bgColor, 0.2)).replace('{bgColor}', bgColor).replace('{imgUrl}', imgUrl);
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

